import { Box, HStack, VStack } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import {
  BiodiversityIcon,
  CircularEconomyIcon,
  ClimateAdaptationIcon,
  ClimateMitigationIcon,
  PollutionPreventionIcon,
  WaterIcon,
} from 'Tokens/Icons/ObjectiveIcons';
import React from 'react';

export type VariantType = typeof OBJECTIVE_VARIANTS;
export type ObjectiveStatusType = typeof STATUS_VARIANTS;
export type TagStatusType = typeof TAG_STATUS;

type ObjectivesTilesType = {
  variant: keyof VariantType;
  objectiveStatus: keyof ObjectiveStatusType;
  tagStatus?: string;
};

export const OBJECTIVE_VARIANTS = {
  mitigation: {
    icon: ClimateMitigationIcon,
    title: 'Climate mitigation',
  },
  adaptation: {
    icon: ClimateAdaptationIcon,
    title: 'Climate adaptation',
  },
  water: {
    icon: WaterIcon,
    title: 'Water',
  },
  circular: {
    icon: CircularEconomyIcon,
    title: 'Circular economy',
  },
  pollution: {
    icon: PollutionPreventionIcon,
    title: 'Pollution prevention',
  },
  biodiversity: {
    icon: BiodiversityIcon,
    title: 'Biodiversity',
  },
};

export const STATUS_VARIANTS = {
  SC: {
    title: 'SC' as const,
    description: 'Substantial contribution',
    borderColor: '',
    bgColor: 'bg.info',
    iconColor: 'text.info',
    iconBgColor: 'bg.info',
    iconOutline: false,
  },
  DNSH: {
    title: 'DNSH' as const,
    description: 'Do no significant harm',
    borderColor: '',
    bgColor: 'bg.muted',
    iconColor: 'text.muted',
    iconBgColor: 'bg.unknown',
    iconOutline: false,
  },
  NotRelevant: {
    title: 'NotRelevant' as const,
    description: 'N/A - Not relevant',
    borderColor: 'border.decorative',
    bgColor: 'bg.default',
    iconColor: 'text.muted',
    iconBgColor: 'bg.unknown',
    iconOutline: true,
  },
};

export const TAG_STATUS = {
  Default: {
    status: '',
  },
  Enabling: {
    status: 'Enabling',
  },
  Transitional: {
    status: 'Transitional',
  },
  CanBeEnabling: {
    status: 'Can be enabling',
  },
  CanBeTransitional: {
    status: 'Can be transitional',
  },
};

export const ObjectivesTiles = ({
  variant,
  objectiveStatus,
  tagStatus,
  size,
}: ObjectivesTilesType & { size?: 'sm' | 'md' }) => {
  const { icon: IconComponent, title } = OBJECTIVE_VARIANTS[variant];
  const { description, bgColor, iconColor, iconBgColor, borderColor, iconOutline } =
    STATUS_VARIANTS[objectiveStatus];

  const isSmall = size === 'sm';

  return (
    <HStack
      width={isSmall ? '284px' : '312px'}
      height={isSmall ? '40px' : '56px'}
      padding={isSmall ? '6px' : '10px'}
      gap={isSmall ? '6px' : '8px'}
      alignItems="start"
      borderRadius="8px"
      bgColor={bgColor}
      border={borderColor ? '1px' : ''}
      borderColor={borderColor ? borderColor : ''}
    >
      <VStack>
        <Box
          backgroundColor={iconBgColor}
          borderRadius="50%"
          w="24px"
          h="24px"
          position="relative"
          border={iconOutline ? '1px' : ''}
          borderColor={iconOutline ? 'border.decorative' : ''}
        >
          <IconComponent
            boxSize="16px"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%,-50%)"
            color={iconColor}
          />
        </Box>
      </VStack>
      <VStack gap="0px" alignItems="start" width="100%">
        <Typography
          variant={isSmall ? 'detailStrong' : 'bodyStrong'}
          color="text.default"
          whiteSpace="nowrap"
        >
          {title}
        </Typography>
        <Typography variant={isSmall ? 'micro' : 'detail'} color="text.muted" whiteSpace="nowrap">
          {description}
        </Typography>
      </VStack>
      <VStack alignItems="start">
        <Typography
          variant={isSmall ? 'microStrong' : 'detailStrong'}
          color="text.info"
          whiteSpace="nowrap"
        >
          {tagStatus}
        </Typography>
      </VStack>
    </HStack>
  );
};
